<template>
    <div class="page" id="lessonRecords">
        <header class="pageHeader rounded">
            <fade-image class="darken" src="assets/headers/lessons.png" />
        </header>

        <div class="scroller2">
            <header>
                <h2>Lesson Records</h2>
                <ul class="links">
                    <li v-show="!busy">
                        <a
                            @click.prevent="toggleShowVideos"
                            :class="{ active: showType == 1 }"
                            class="video"
                            href="#"
                        >
                            <img src="@/assets/videolesson/video-icon.svg" v-show="!showType" />
                            <img
                                src="@/assets/videolesson/video-icon-active.svg"
                                v-show="showType"
                            />
                        </a>
                    </li>
                    <li>
                        <button
                            v-show="!busy"
                            @click.prevent="filterStarredLessons"
                            class="starButton"
                        >
                            <img v-if="!starFilter" src="@/assets/icons/star-unfilled.svg" />
                            <img v-else src="@/assets/icons/star-fill.svg" />
                        </button>
                        <svg
                            class="loader"
                            v-show="busy"
                            xmlns="http://www.w3.org/2000/svg"
                            xmlns:xlink="http://www.w3.org/1999/xlink"
                            style="margin: auto; display: block; shape-rendering: auto"
                            width="100px"
                            height="100px"
                            viewBox="0 0 100 100"
                            preserveAspectRatio="xMidYMid"
                        >
                            <circle
                                cx="50"
                                cy="50"
                                fill="none"
                                stroke="#61c45c"
                                stroke-width="10"
                                r="22"
                                stroke-dasharray="103.67255756846316 36.55751918948772"
                            >
                                <animateTransform
                                    attributeName="transform"
                                    type="rotate"
                                    repeatCount="indefinite"
                                    dur="1s"
                                    values="0 50 50;360 50 50"
                                    keyTimes="0;1"
                                ></animateTransform>
                            </circle>
                        </svg>
                    </li>

                    <li>
                        <a class="cancel" @click.prevent="$router.back()" href="#">
                            <x-icon class="icon" />
                        </a>
                    </li>
                </ul>
            </header>

            <span v-if="pastLessons.length == 0">
                <empty-placeholder class="mb10">
                    TIP: You have no lesson history to view yet! Book a lesson today and come back
                    and view the notes here after your Pro has added them to your account.
                </empty-placeholder>

                <router-link
                    class="button"
                    :to="{ name: 'BookChooseType' }"
                    v-if="$store.state.user.lesson_settings.video_lessons_enabled"
                >
                    Book a lesson
                    <img
                        src="@/assets/arrow-right-white-2.svg"
                        style="float: right"
                        class="arrow"
                    />
                </router-link>
                <router-link :to="{ name: 'BookLesson' }" class="button" v-else>
                    Book a lesson
                    <img
                        src="@/assets/arrow-right-white-2.svg"
                        style="float: right"
                        class="arrow"
                    />
                </router-link>
            </span>

            <ul class="lessonList">
                <li
                    class="historyLesson"
                    v-for="lesson in pastLessons"
                    :key="lesson.id"
                    :class="{
                        noRecord: showCreateLesson(lesson),
                        'type-0': lesson.type == 0,
                        'type-1': lesson.type == 1,
                    }"
                >
                    <router-link
                        :to="{
                            name: 'playerLesson',
                            params: { id: lesson.id },
                        }"
                    >
                        <img
                            class="record-type-video-icon"
                            v-show="lesson.type == 1 && !showCreateLesson(lesson)"
                            src="@/assets/videolesson/video-record-icon.svg"
                        />
                        <span v-if="showCreateLesson(lesson)">
                            {{ $date(lesson.date).format("DD MMM YYYY") }}
                            <strong>Awaiting record update</strong>
                            <img class="recordArrow" src="@/assets/arrow-right-white-2.svg" />
                        </span>
                        <span v-else>
                            <b
                                ><span v-if="!lesson.title">Untitled record</span
                                ><span class="title" v-else>{{ lesson.title }}</span></b
                            >
                            {{ $date(lesson.date).format("DD MMM YYYY") }}

                            <template v-if="lesson.start != lesson.end">
                                {{ $date("2020-12-08 " + lesson.start).format("HH:mm") }}
                                -
                                {{ $date("2020-12-08 " + lesson.end).format("HH:mm") }}
                            </template>

                            <lesson-star :lesson="lesson" />
                            <img class="recordArrow" src="@/assets/arrow-right-white.svg" />
                        </span>
                    </router-link>

                    <lesson-locked-overlay v-if="isLessonRecordLocked(lesson)" :lesson="lesson" />
                </li>
            </ul>

            <upgrade-to-premium-button v-if="pastLessons.length > 0 && !isPremium" />
        </div>
    </div>
</template>

<script>
import EmptyPlaceholder from "@/components/App/empty-placeholder";
import LessonStar from "@/components/Player/Lessons/lesson-star";
import FadeImage from "@/components/App/fade-image";
import { XIcon } from "vue-feather-icons";
import LessonLockedOverlay from "@/components/Player/Lessons/lesson-locked-overlay";
import UpgradeToPremiumButton from "@/components/Player/upgrade-to-premium-button";
import lessonRecordAccess from "@/mixins/lesson-record-access";
export default {
    name: "book",
    mixins: [lessonRecordAccess],
    components: {
        EmptyPlaceholder,
        FadeImage,
        XIcon,
        LessonStar,
        LessonLockedOverlay,
        UpgradeToPremiumButton,
    },
    created() {
        this.getPlayerHistoryLessons();
    },
    data() {
        return {
            pastLessons: [],
            starFilter: false,
            busy: false,
            showType: null,
        };
    },
    methods: {
        getPlayerHistoryLessons() {
            this.busy = true;
            var isStarred = this.starFilter ? "&starred=" + this.starFilter : "";
            window.axios
                .get("/player/" + this.$store.state.user.id + "/lessons?history=true" + isStarred)
                .then((response) => {
                    this.pastLessons = response.data;

                    if (this.showType) {
                        this.pastLessons = this.pastLessons.filter((lesson) => {
                            return lesson.type == this.showType;
                        });
                    }
                })
                .finally(() => {
                    this.busy = false;
                });
        },
        filterStarredLessons() {
            this.starFilter = !this.starFilter;
            this.getPlayerHistoryLessons();
        },
        toggleShowVideos() {
            if (!this.showType) {
                this.showType = 1;
            } else {
                this.showType = null;
            }
            this.getPlayerHistoryLessons();
        },
        showCreateLesson(lesson) {
            if (!lesson.record) {
                return true;
            }
            let nonPlayerRecords = lesson.record.filter((record) => !record.is_player_submitted);
            return nonPlayerRecords.length == 0;
        },
    },
};
</script>

<style lang="scss" scoped>
.record-type-video-icon {
    position: absolute;
    top: -4px;
    right: 15px;
}
.starButton {
    transform: translateY(-4px) translateX(-4px);
}

.links {
    .video {
        position: absolute;
        right: 70px;
        top: -8px;
        padding: 13px;
    }
}
.loader {
    width: 34px;
    height: 35px;
    position: absolute;
    right: 32px;
    top: -6px;
}

.historyLesson {
    background: white;
    border-radius: $radius;
    margin-bottom: 10px;
    padding: 17px 20px;
    position: relative;

    &.type-1 {
        background-color: #fbf9f4 !important;

        &.noRecord {
            background-color: #444745 !important;
        }
    }

    a {
        text-decoration: none;
        color: $green_0;
    }
    &.noRecord {
        background: $green_0;
        color: white;
        font-weight: 200;
        a {
            color: white;
        }
        strong {
            font-weight: 400;
            display: block;
            margin-top: 10px;
        }
    }
    b {
        font-weight: 400;
        margin-bottom: 10px;
        display: block;
    }
    .recordArrow {
        position: absolute;
        right: 15px;
        bottom: 20px;
        pointer-events: none;
    }
    .star {
        position: absolute;
        right: 35px;
        bottom: 14px;
        top: auto;
    }
}
.title {
    line-height: 1.5;
}
</style>
